import classnames from 'classnames/bind';
import { KeyboardEvent, ReactNode, useCallback, useMemo } from 'react';
import { memo } from 'react';
import styles from './LayoutSwitch.module.scss';
import { SegmentedItem } from './SegmentedItem';

const cx = classnames.bind(styles);

export type LayoutSwitchOption<TLayout extends string> = {
  value: TLayout;
  /** Icon-only item, so this is also the item's accessible name. */
  label: string;
  /** Text shown in the hover tooltip; falls back to `label` when omitted. */
  tooltip?: string;
  icon: ReactNode;
  testId?: string;
};

export type Props<TLayout extends string> = {
  layout: TLayout;
  onChange: (layout: TLayout) => void;
  /**
   * Ordered left-to-right. Order also decides arrow-key traversal, so the
   * two always agree on which option is "next".
   */
  options: LayoutSwitchOption<TLayout>[];
  /** Accessible name for the `radiogroup` (e.g. "Payment detail display format"). */
  ariaLabel: string;
};

/**
 * Generic segmented table/card layout toggle, shared across sections that
 * offer both layouts (e.g. 支払明細, 仕訳). Feature code supplies the
 * translated labels/aria-label and the ordered option list; this component
 * owns only the radiogroup semantics and arrow-key traversal.
 */
const _LayoutSwitch = <TLayout extends string>({
  layout,
  onChange,
  options,
  ariaLabel,
}: Props<TLayout>) => {
  const order = useMemo(() => options.map((option) => option.value), [options]);

  // Arrow keys move between options while the group stays one tab stop.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const step =
        event.key === 'ArrowRight' || event.key === 'ArrowDown'
          ? 1
          : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
      if (step === 0) return;

      event.preventDefault();
      const current = order.indexOf(layout);
      const next = order[(current + step + order.length) % order.length];
      if (next) onChange(next);
    },
    [layout, onChange, order]
  );

  return (
    <div
      role='radiogroup'
      aria-label={ariaLabel}
      className={cx('segmented')}
      onKeyDown={handleKeyDown}
    >
      {options.map((option) => (
        <SegmentedItem
          key={option.value}
          label={option.label}
          tooltip={option.tooltip ?? option.label}
          icon={option.icon}
          selected={layout === option.value}
          onSelect={() => onChange(option.value)}
          testId={option.testId}
        />
      ))}
    </div>
  );
};
_LayoutSwitch.displayName = '_LayoutSwitch';

// The generic parameter is erased by `memo`; the assertion restores it.
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const LayoutSwitch = memo(_LayoutSwitch) as typeof _LayoutSwitch;
